import { DesignData, ProductVariant } from 'src/store/design/types';
import i18n from 'src/i18n';
import getUsedSpreads from './getUsedSpreads';

export interface DisabledSpreadInfo {
  spreadIndex: number;
  information: string;
}

/**
 * Calculates which print areas should be disabled based on the variants and current design content
 * @param designData - The current design structure
 * @returns Array of disabled spread information for each spread in the design
 */
const calculateDisabledPrintAreas = (designData?: DesignData): DisabledSpreadInfo[] => {
  if (!designData?.variants?.length) {
    return [];
  }

  // Get spread names that have actual content (used print areas)
  const usedSpreadNames = getUsedSpreads(designData);

  if (!usedSpreadNames.size) {
    // No content yet, nothing to disable
    return [];
  }

  // Find all variants that match the used spread names
  const matchingVariants = findMatchingVariants(designData.variants, usedSpreadNames);

  if (!matchingVariants.length) {
    // No matching variants found, can't determine what to disable
    return [];
  }

  // Get all spread names that are available in any of the matching variants
  const availableSpreadNames = new Set<string>();
  matchingVariants.forEach((variant) => {
    variant.types.forEach((spreadName) => availableSpreadNames.add(spreadName));
  });

  // Go through all spreads and determine if each one is disabled
  const disabledSpreads: DisabledSpreadInfo[] = designData.spreads.map((spread, index) => ({
    spreadIndex: availableSpreadNames.has(spread.name) ? -1 : index,
    information: i18n.t('txt_unavailable_print_area'),
  }));

  return disabledSpreads;
};

/**
 * Finds all variants that contain all used spread names
 */
function findMatchingVariants(variants: ProductVariant[], usedSpreadNames: Set<string>): ProductVariant[] {
  const usedSpreadNamesArray = Array.from(usedSpreadNames);

  // Find all variants that contain all used spread names
  return variants.filter((variant) => usedSpreadNamesArray.every((spreadName) => variant.types.includes(spreadName)));
}

export default calculateDisabledPrintAreas;
